import React, { FC, useMemo } from 'react';
import { Box, Typography, Button, Skeleton } from '@mui/material';
import { NavLink } from 'react-router-dom';
import { usePriceFormatter } from '../../../../hooks';

interface CashoutCardProps {
    amount?: number;
    loading?: boolean;
    cashoutRoute?: string;
}

const CashoutCard: FC<CashoutCardProps> = ({
    amount = 0,
    loading = false,
    cashoutRoute = '/cash-out',
}) => {
    const formatter = usePriceFormatter();

    if (!loading && amount <= 0) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                my: 2.5,
                alignItems: 'center',
                justifyContent: 'space-between',
                px: { xs: 2, sm: 3 },
                gap: 3,
                py: 1.5,
                width: '440px',
                height: 109,
                background: 'rgba(78, 223, 109, 0.1)',
                border: '1px solid #4EDF6D',
                borderRadius: '20px',
            }}
            role="region"
            aria-label="Available balance"
        >
            {/* LEFT SECTION */}
            <Box display="flex" flexDirection="column" gap={0.5}>
                {loading ? (
                    <>
                        <Skeleton width={120} height={18} />
                        <Skeleton width={100} height={28} />
                    </>
                ) : (
                    <>
                        <Typography
                            sx={{
                                fontFamily: 'Avenir',
                                fontSize: 14,
                                fontWeight: 400,
                                color: '#FFFFFF',
                            }}
                        >
                            Available Balance
                        </Typography>

                        <Typography
                            sx={{
                                fontFamily: 'Avenir',
                                fontSize: 18,
                                fontWeight: 800,
                                color: '#4EDF6D',
                                lineHeight: '25px',
                            }}
                        >
                            {formatter.balance(amount).display}
                        </Typography>
                    </>
                )}
            </Box>

            {/* RIGHT SECTION */}
            {loading ? (
                <Skeleton
                    variant="rounded"
                    width={154}
                    height={36}
                    sx={{ borderRadius: '1234px' }}
                />
            ) : (
                <NavLink to={cashoutRoute} style={{ textDecoration: 'none' }}>
                    <Button
                        variant="contained"
                        sx={{
                            width: 154,
                            height: 36,
                            borderRadius: '1234px',
                            backgroundColor: '#4EDF6D',
                            fontFamily: 'Avenir',
                            fontSize: 14,
                            fontWeight: 800,
                            color: '#141414',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#3ddc6f',
                            },
                        }}
                    >
                        Cash Out
                    </Button>
                </NavLink>
            )}
        </Box>
    );
};

export default React.memo(CashoutCard);
